import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    AlertCircle,
    Check,
    Copy,
    Film,
    Image as ImageIcon,
    Loader2,
    RefreshCw,
    Save,
    Trash2,
    UploadCloud,
} from "lucide-react";
import { BrandButton } from "@/components/ui/brand-button";
import { cn } from "@/lib/utils";
import { extractErrorMessage, fromCsv, toCsv, formatBytes } from "@/lib/admin-utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MEDIA_API = `${BACKEND_URL}/api/media`;

const inputCls =
    "block w-full bg-white border border-brand-dark/15 px-3 py-2 text-sm text-brand-dark placeholder:text-brand-gray focus:border-brand-cyan focus:outline-none transition-colors";

const AssetCard = ({ asset, token, onChanged, onDeleted }) => {
    const [alt, setAlt] = useState(asset.alt_text || "");
    const [tags, setTags] = useState(toCsv(asset.tags));
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [copied, setCopied] = useState(false);
    const [err, setErr] = useState("");
    const headers = { "X-Admin-Token": token };

    const save = async () => {
        setSaving(true);
        setErr("");
        try {
            const res = await axios.put(
                `${MEDIA_API}/${asset.id}`,
                { alt_text: alt, tags: fromCsv(tags) },
                { headers },
            );
            onChanged(res.data);
        } catch (e) {
            setErr(extractErrorMessage(e, "Save failed."));
        } finally {
            setSaving(false);
        }
    };

    const del = async () => {
        if (!window.confirm(`Delete "${asset.original_filename || asset.filename}"? This cannot be undone.`)) return;
        setDeleting(true);
        setErr("");
        try {
            await axios.delete(`${MEDIA_API}/${asset.id}`, { headers });
            onDeleted(asset.id);
        } catch (e) {
            setErr(extractErrorMessage(e, "Delete failed."));
            setDeleting(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(asset.url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* clipboard unavailable — user can select the text manually */
        }
    };

    return (
        <div
            className="bg-white border border-brand-dark/10 flex flex-col"
            data-testid={`admin-media-card-${asset.id}`}
        >
            <div className="aspect-video bg-brand-offwhite overflow-hidden flex items-center justify-center">
                {asset.media_type === "image" ? (
                    <img
                        src={asset.url}
                        alt={asset.alt_text || asset.filename}
                        className="h-full w-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <video src={asset.url} controls muted className="h-full w-full object-cover" />
                )}
            </div>
            <div className="p-3 space-y-2 flex-1 flex flex-col">
                <p className="text-xs font-semibold text-brand-dark truncate" title={asset.original_filename || asset.filename}>
                    {asset.original_filename || asset.filename}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-brand-gray">
                    {asset.media_type} · {formatBytes(asset.size_bytes)}
                    {asset.width ? ` · ${asset.width}×${asset.height}` : ""}
                </p>
                <button
                    type="button"
                    onClick={copy}
                    className="inline-flex items-start gap-1 text-[11px] font-mono text-brand-cyan hover:text-brand-dark text-left break-all transition-colors"
                    title="Copy URL"
                    data-testid={`admin-media-copy-${asset.id}`}
                >
                    {copied ? (
                        <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={2.5} />
                    ) : (
                        <Copy className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={2.25} />
                    )}
                    {copied ? "Copied!" : asset.url}
                </button>
                <input
                    className={inputCls}
                    value={alt}
                    onChange={(e) => setAlt(e.target.value)}
                    placeholder="Alt text (describe the image)"
                    data-testid={`admin-media-alt-${asset.id}`}
                />
                <input
                    className={inputCls}
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="tags, comma, separated"
                    data-testid={`admin-media-tags-${asset.id}`}
                />
                <div className="flex items-center gap-2 mt-auto pt-1">
                    <BrandButton
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={save}
                        disabled={saving}
                        data-testid={`admin-media-save-${asset.id}`}
                    >
                        {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                        ) : (
                            <Save className="h-4 w-4" strokeWidth={2.25} />
                        )}
                        Save
                    </BrandButton>
                    <button
                        type="button"
                        onClick={del}
                        disabled={deleting}
                        className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-brand-coral hover:text-brand-dark transition-colors disabled:opacity-50"
                        data-testid={`admin-media-delete-${asset.id}`}
                    >
                        {deleting ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
                        ) : (
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                        )}
                        Delete
                    </button>
                </div>
                {err && (
                    <p className="text-xs text-brand-coral flex items-start gap-1">
                        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={2.5} />
                        {err}
                    </p>
                )}
            </div>
        </div>
    );
};

const AdminMediaPanel = ({ token }) => {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [typeFilter, setTypeFilter] = useState(""); // "" | image | video
    const [file, setFile] = useState(null);
    const [uploadAlt, setUploadAlt] = useState("");
    const [uploadTags, setUploadTags] = useState("");
    const [uploading, setUploading] = useState(false);
    const [uploadErr, setUploadErr] = useState("");

    const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

    const fetchAssets = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(MEDIA_API, {
                headers,
                params: typeFilter ? { media_type: typeFilter } : {},
            });
            setAssets(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setError(extractErrorMessage(e, "Could not load media."));
        } finally {
            setLoading(false);
        }
    }, [headers, typeFilter]);

    useEffect(() => {
        fetchAssets();
    }, [fetchAssets]);

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) return;
        setUploading(true);
        setUploadErr("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("alt_text", uploadAlt);
            fd.append("tags", uploadTags);
            // Let axios set the multipart boundary — only forward the auth header.
            await axios.post(`${MEDIA_API}/upload`, fd, { headers });
            setFile(null);
            setUploadAlt("");
            setUploadTags("");
            if (e.target && typeof e.target.reset === "function") e.target.reset();
            await fetchAssets();
        } catch (err) {
            setUploadErr(extractErrorMessage(err, "Upload failed."));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div data-testid="admin-media-panel">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-display text-3xl tracking-wide text-brand-dark leading-none">
                        Media Library
                    </h2>
                    <p className="mt-2 text-sm text-brand-dark/70">
                        Upload images &amp; videos, then copy a URL to use in the Page Content or Blog editors.
                    </p>
                </div>
                <BrandButton
                    variant="outline"
                    tone="onLight"
                    size="md"
                    onClick={fetchAssets}
                    disabled={loading}
                    data-testid="admin-media-refresh"
                >
                    {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                    ) : (
                        <RefreshCw className="h-4 w-4" strokeWidth={2.25} />
                    )}
                    Refresh
                </BrandButton>
            </div>

            {/* Upload form */}
            <form
                onSubmit={handleUpload}
                className="bg-white border border-brand-dark/10 p-5 md:p-6 mb-6"
                data-testid="admin-media-upload-form"
            >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <label className="block md:col-span-2">
                        <span className="block text-[10px] uppercase tracking-wider font-semibold text-brand-gray mb-1">
                            File (image or video)
                        </span>
                        <input
                            type="file"
                            accept="image/*,video/*"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="block w-full text-sm text-brand-dark file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-brand-dark file:text-white file:text-xs file:uppercase file:tracking-wider file:font-semibold hover:file:bg-black file:cursor-pointer"
                            data-testid="admin-media-file"
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-wider font-semibold text-brand-gray mb-1">
                            Alt text
                        </span>
                        <input
                            className={inputCls}
                            value={uploadAlt}
                            onChange={(e) => setUploadAlt(e.target.value)}
                            placeholder="Describe the image"
                            data-testid="admin-media-upload-alt"
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-wider font-semibold text-brand-gray mb-1">
                            Tags
                        </span>
                        <input
                            className={inputCls}
                            value={uploadTags}
                            onChange={(e) => setUploadTags(e.target.value)}
                            placeholder="fencing, hero"
                            data-testid="admin-media-upload-tags"
                        />
                    </label>
                </div>
                <div className="flex items-center gap-3 mt-4">
                    <BrandButton
                        type="submit"
                        variant="primary"
                        size="md"
                        disabled={!file || uploading}
                        data-testid="admin-media-upload-submit"
                    >
                        {uploading ? (
                            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                        ) : (
                            <UploadCloud className="h-4 w-4" strokeWidth={2.25} />
                        )}
                        Upload
                    </BrandButton>
                    <span className="text-[11px] text-brand-gray">
                        JPEG / PNG / WebP / GIF / MP4 / WebM / MOV. Max 15 MB image, 200 MB video.
                    </span>
                </div>
                {uploadErr && (
                    <p className="mt-3 flex items-start gap-1.5 text-xs text-brand-coral" data-testid="admin-media-upload-error">
                        <AlertCircle className="h-3.5 w-3.5 mt-0.5" strokeWidth={2.5} />
                        {uploadErr}
                    </p>
                )}
            </form>

            {/* Type filter */}
            <div className="flex items-center gap-2 mb-5" role="tablist" aria-label="Filter media by type">
                {[
                    { key: "", label: "All", Icon: null },
                    { key: "image", label: "Images", Icon: ImageIcon },
                    { key: "video", label: "Videos", Icon: Film },
                ].map((f) => {
                    const active = typeFilter === f.key;
                    return (
                        <button
                            key={f.key || "all"}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setTypeFilter(f.key)}
                            className={cn(
                                "inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold border transition-colors",
                                active
                                    ? "bg-brand-dark text-white border-brand-dark"
                                    : "bg-white text-brand-dark border-brand-dark/15 hover:border-brand-cyan hover:text-brand-cyan",
                            )}
                            data-testid={`admin-media-type-${f.key || "all"}`}
                        >
                            {f.Icon && <f.Icon className="h-3.5 w-3.5" strokeWidth={2.25} />}
                            {f.label}
                        </button>
                    );
                })}
            </div>

            {error && (
                <div className="mb-6 border-l-4 border-brand-coral bg-brand-coral/10 p-4 text-sm text-brand-dark" data-testid="admin-media-error">
                    <p className="font-semibold flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-brand-coral" strokeWidth={2.5} />
                        {error}
                    </p>
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-8 w-8 text-brand-cyan animate-spin" strokeWidth={2} />
                </div>
            ) : assets.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-brand-dark/15 p-16 text-center" data-testid="admin-media-empty">
                    <ImageIcon className="h-10 w-10 mx-auto text-brand-gray mb-4" strokeWidth={1.5} />
                    <h3 className="font-display text-2xl tracking-wide text-brand-dark mb-2">No media yet.</h3>
                    <p className="text-sm text-brand-dark/60">Upload your first image or video above.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="admin-media-grid">
                    {assets.map((asset) => (
                        <AssetCard
                            key={asset.id}
                            asset={asset}
                            token={token}
                            onChanged={(updated) =>
                                setAssets((prev) => prev.map((a) => (a.id === updated.id ? updated : a)))
                            }
                            onDeleted={(id) => setAssets((prev) => prev.filter((a) => a.id !== id))}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminMediaPanel;
